/**
 * Avatar Dropdown Component
 *
 * Dropdown menu for authenticated user avatar with account-related navigation
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@vsat/shared/store/authStore';
import { useTranslation } from 'react-i18next';

interface AvatarDropdownProps {
  userInitials: string;
  userName?: string;
}

export function AvatarDropdown({ userInitials, userName }: AvatarDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { logout, user } = useAuthStore();
  const { t } = useTranslation();

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close dropdown on ESC key
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showLogoutConfirm) {
          setShowLogoutConfirm(false);
        } else if (isOpen) {
          setIsOpen(false);
          buttonRef.current?.focus();
        }
      }
    };

    if (isOpen || showLogoutConfirm) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [isOpen, showLogoutConfirm]);

  const handleMenuClick = () => {
    setIsOpen(false);
  };

  const handleLogoutClick = () => {
    setIsOpen(false);
    setShowLogoutConfirm(true);
  };

  const handleConfirmLogout = () => {
    setShowLogoutConfirm(false);
    logout();
    navigate('/');
  };

  const handleCancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  return (
    <>
      <div className='relative'>
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          className='flex items-center gap-2 cursor-pointer group focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-full'
          aria-expanded={isOpen}
          aria-haspopup='true'
          aria-label='User menu'
        >
          <div className='w-8 h-8 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 border border-orange-200 flex items-center justify-center text-orange-700 font-bold text-xs group-hover:ring-2 ring-orange-100 transition-all'>
            {userInitials}
          </div>
          <span className='material-symbols-outlined text-[#64748B] dark:text-[#94A3B8] group-hover:text-primary transition-colors text-[20px]'>
            expand_more
          </span>
        </button>

        {isOpen && (
          <div
            ref={dropdownRef}
            className='absolute right-0 mt-2 w-56 bg-white dark:bg-[#1E293B] rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50'
            role='menu'
            aria-orientation='vertical'
          >
            {userName && (
              <div className='px-4 py-2 border-b border-gray-200 dark:border-gray-700'>
                <p className='text-sm font-semibold text-gray-900 dark:text-white'>{userName}</p>
                {user?.email && (
                  <p className='text-xs text-gray-500 dark:text-gray-400 truncate'>{user.email}</p>
                )}
              </div>
            )}

            <Link
              to='/account'
              onClick={handleMenuClick}
              className='block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:bg-gray-100 dark:focus:bg-gray-800 focus:outline-none'
              role='menuitem'
            >
              <div className='flex items-center gap-2'>
                <span className='material-symbols-outlined text-[18px]'>person</span>
                <span>{t('nav.account')}</span>
              </div>
            </Link>

            <Link
              to='/assessment-history'
              onClick={handleMenuClick}
              className='block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:bg-gray-100 dark:focus:bg-gray-800 focus:outline-none'
              role='menuitem'
            >
              <div className='flex items-center gap-2'>
                <span className='material-symbols-outlined text-[18px]'>history</span>
                <span>{t('nav.history')}</span>
              </div>
            </Link>

            <Link
              to='/help'
              onClick={handleMenuClick}
              className='block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:bg-gray-100 dark:focus:bg-gray-800 focus:outline-none'
              role='menuitem'
            >
              <div className='flex items-center gap-2'>
                <span className='material-symbols-outlined text-[18px]'>help_outline</span>
                <span>{t('nav.help')}</span>
              </div>
            </Link>

            <div className='border-t border-gray-200 dark:border-gray-700 my-1'></div>

            <button
              onClick={handleLogoutClick}
              className='w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:bg-gray-100 dark:focus:bg-gray-800 focus:outline-none'
              role='menuitem'
            >
              <div className='flex items-center gap-2'>
                <span className='material-symbols-outlined text-[18px]'>logout</span>
                <span>{t('nav.logout')}</span>
              </div>
            </button>
          </div>
        )}
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className='fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm'
              onClick={handleCancelLogout}
            />

            {/* Modal Content */}
            <div className='fixed inset-0 z-[201] flex items-center justify-center p-4 pointer-events-none'>
              <div
                className='bg-white dark:bg-[#1E293B] rounded-lg shadow-xl max-w-md w-full p-6 pointer-events-auto'
                onClick={e => e.stopPropagation()}
              >
                <div className='flex items-start gap-4'>
                  <div className='flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center'>
                    <span className='material-symbols-outlined text-red-600 dark:text-red-400 text-[24px]'>
                      logout
                    </span>
                  </div>
                  <div className='flex-1'>
                    <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-2'>
                      {t('nav.logout')}
                    </h3>
                    <p className='text-sm text-gray-600 dark:text-gray-400'>
                      {t('nav.confirmLogout')}
                    </p>
                  </div>
                </div>

                <div className='flex gap-3 mt-6'>
                  <button
                    onClick={handleCancelLogout}
                    className='flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400'
                  >
                    Hủy
                  </button>
                  <button
                    onClick={handleConfirmLogout}
                    className='flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-red-500'
                  >
                    Đăng xuất
                  </button>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
}
